import { Link } from "@/lib/router-compat";
import { Calendar, Component, Gift, Search, ShoppingCart, Sparkles, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { HeroButton } from "@/hooks/useHeroContent";

type Props = {
  buttons?: HeroButton[] | undefined;
  /** rendered when no CMS buttons are supplied — keeps legacy hard-coded CTAs working */
  fallback?: React.ReactNode;
  className?: string;
};

const HERO_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Calendar,
  Component,
  Gift,
  Search,
  ShoppingCart,
  Sparkles,
  Wrench,
};

function variantToButtonVariant(v?: HeroButton["variant"]) {
  switch (v) {
    case "secondary":
      return "secondary" as const;
    case "ghost":
      return "ghost" as const;
    case "primary":
    default:
      return "default" as const;
  }
}

function isExternal(href: string) {
  return /^https?:\/\//i.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
}

const HeroButtons = ({ buttons, fallback, className }: Props) => {
  const visible = (buttons ?? []).filter((b) => b && b.label && b.href && b.visible !== false);
  if (visible.length === 0) return <>{fallback ?? null}</>;

  return (
    <div className={`flex flex-wrap items-center justify-center gap-4 ${className ?? ""}`}>
      {visible.map((b, i) => {
        const Icon = b.icon ? HERO_ICONS[b.icon] ?? null : null;
        const inner = (
          <span className="inline-flex items-center gap-2">
            {Icon ? <Icon className="h-4 w-4" /> : null}
            {b.label}
          </span>
        );
        const external = isExternal(b.href);
        return (
          <Button
            key={`${b.label}-${i}`}
            asChild
            size="lg"
            variant={variantToButtonVariant(b.variant)}
            className="font-heading uppercase tracking-wide"
          >
            {external ? (
              <a
                href={b.href}
                target={b.open_in_new_tab ? "_blank" : undefined}
                rel={b.open_in_new_tab ? "noopener noreferrer" : undefined}
              >
                {inner}
              </a>
            ) : (
              <Link
                to={b.href}
                {...(b.open_in_new_tab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              >
                {inner}
              </Link>
            )}
          </Button>
        );
      })}
    </div>
  );
};

export default HeroButtons;